import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useBranding } from '../contexts/BrandingContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { toast } from './ui/sonner';
import NotificationsBell from './NotificationsBell';
import SubscriptionBanner from './SubscriptionBanner';
import CompanyLogo from './CompanyLogo';
import BottomNav from './BottomNav';
import AnnouncementBroadcastCard from './AnnouncementBroadcastCard';
import ReviewRoleSwitcher from './ReviewRoleSwitcher';
import CompanyDeletionBanner from './CompanyDeletionBanner';
import TermsAcceptanceGate from './TermsAcceptanceGate';
import { ThemeToggle } from './ThemeToggle';
import { isNativeApp } from '../lib/native';
import {
  LogOut, Truck, Users, ClipboardList, DollarSign, BarChart3, CreditCard, FileText,
  MessageSquare, MessageSquareWarning, MessageCircleQuestion, MessagesSquare, Menu, X, ChevronRight, ChevronDown, LayoutDashboard, Wrench, CalendarDays, TrendingUp, Activity,
  User as UserIcon, Briefcase, Settings as SettingsIcon, Camera, ShieldCheck, ShieldAlert, Sparkles, Trash2, Palette,
  Building2, Receipt, UsersRound, LineChart, Cog, Upload, Inbox, Rocket, UserPlus, Shield, CalendarCheck, Megaphone, GraduationCap,
  PiggyBank, Layers, CheckCircle, Mail,
  MapPin, Scale, FileSignature,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Items keyed by id so we can permission-gate analytics features per company.
// Groupings are intentional: each grouped child is auto-expanded by NavGroup
// when its route is active, others stay collapsed for a clean shell.
const NAV_BY_ROLE = {
  super_admin: [
    { id: 'dashboard', to: '/dashboard', label: 'Overview', icon: LayoutDashboard, testid: 'nav-dashboard' },
    { id: 'launch-setup', to: '/admin/setup', label: 'Launch checklist', icon: Rocket, testid: 'nav-launch-setup' },
    {
      id: 'group-platform', label: 'Platform Operations', icon: Building2, testid: 'nav-group-platform',
      children: [
        { id: 'companies', to: '/admin/companies', label: 'Companies', icon: Building2, testid: 'nav-companies' },
        { id: 'access-requests', to: '/admin/access-requests', label: 'Access Requests', icon: UserPlus, testid: 'nav-access-requests' },
        { id: 'subscriptions', to: '/admin/subscriptions', label: 'Subscriptions', icon: CreditCard, testid: 'nav-subscriptions' },
        { id: 'delivery-logs', to: '/admin/delivery-logs', label: 'Delivery Log', icon: Mail, testid: 'nav-delivery-logs' },
        { id: 'email-templates', to: '/admin/email-templates', label: 'Email Templates', icon: Mail, testid: 'nav-email-templates' },
        { id: 'legal', to: '/admin/legal', label: 'Legal Documents', icon: Scale, testid: 'nav-legal' },
        { id: 'conversion', to: '/admin/conversion', label: 'Trial Conversion', icon: BarChart3, testid: 'nav-conversion' },
        { id: 'audit-compliance', to: '/admin/compliance', label: 'Compliance', icon: ShieldCheck, testid: 'nav-audit-compliance' },
        { id: 'seed-tenant', to: '/admin/seed-tenant', label: 'Seed Demo Tenant', icon: Sparkles, testid: 'nav-seed-tenant' },
        { id: 'demo-tenants', to: '/admin/demo-tenants', label: 'Demo Tenants', icon: Trash2, testid: 'nav-demo-tenants' },
        { id: 'factory-reset', to: '/admin/factory-reset', label: 'Factory Reset', icon: ShieldAlert, testid: 'nav-factory-reset' },
      ],
    },
    {
      id: 'group-product-insights', label: 'Product Insights', icon: LineChart, testid: 'nav-group-product-insights',
      children: [
        { id: 'telemetry', to: '/admin/telemetry', label: 'Telemetry', icon: Activity, testid: 'nav-telemetry' },
        { id: 'feedback-inbox', to: '/feedback-inbox', label: 'Feedback', icon: MessageCircleQuestion, testid: 'nav-feedback-inbox' },
      ],
    },
    {
      id: 'group-tax-compliance', label: 'Tax & Compliance', icon: ShieldCheck, testid: 'nav-group-tax-compliance',
      children: [
        { id: 'w9-forms', to: '/admin/w9', label: 'W-9 Forms', icon: FileText, testid: 'nav-w9-forms' },
        { id: 'form-1099', to: '/admin/form-1099', label: '1099-NEC Forms', icon: FileText, testid: 'nav-form-1099' },
      ],
    },
  ],
  company_admin: [
    { id: 'dashboard', to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-dashboard' },
    {
      id: 'group-fleet-ops', label: 'Fleet Operations', icon: Truck, testid: 'nav-group-fleet-ops',
      children: [
        { id: 'loads', to: '/loads', label: 'Loads', icon: ClipboardList, testid: 'nav-loads' },
        { id: 'live-tracking', to: '/live-tracking', label: 'Live Tracking', icon: MapPin, testid: 'nav-live-tracking' },
        { id: 'tenders', to: '/tenders', label: 'Tender Inbox', icon: Inbox, testid: 'nav-tenders' },
        { id: 'drivers', to: '/drivers', label: 'Drivers', icon: Users, testid: 'nav-drivers' },
        { id: 'driver-requests', to: '/requests', label: 'Driver Requests', icon: Inbox, testid: 'nav-driver-requests' },
        { id: 'team', to: '/team', label: 'Team members', icon: Shield, testid: 'nav-team' },
        { id: 'compliance-center', to: '/compliance-center', label: 'Compliance Center', icon: CalendarCheck, testid: 'nav-compliance-center' },
        { id: 'training', to: '/training', label: 'Training Center', icon: GraduationCap, testid: 'nav-training' },
        { id: 'manage-staff', to: '/admin/manage-staff', label: 'Manage staff', icon: UserPlus, testid: 'nav-manage-staff' },
        { id: 'contracts', to: '/contracts', label: 'Contracts & Agreements', icon: FileSignature, testid: 'nav-contracts' },
        { id: 'brokers', to: '/brokers', label: 'Brokers', icon: Briefcase, testid: 'nav-brokers' },
      ],
    },
    {
      id: 'group-assets', label: 'Asset Management', icon: Wrench, testid: 'nav-group-assets',
      children: [
        { id: 'assets', to: '/assets', label: 'Assets', icon: Wrench, testid: 'nav-assets' },
        { id: 'fleet-vehicles', to: '/fleet/vehicles', label: 'Fleet Vehicles (ELD)', icon: Truck, testid: 'nav-fleet-vehicles' },
        { id: 'truck_pnl', to: '/truck-pnl', label: 'Truck P&L', icon: TrendingUp, testid: 'nav-pnl' },
      ],
    },
    {
      id: 'group-leave', label: 'Leave', icon: CalendarDays, testid: 'nav-group-leave',
      children: [
        { id: 'my-leave', to: '/my-leave', label: 'My Leave', icon: UserIcon, testid: 'nav-my-leave' },
        { id: 'leave-requests', to: '/leave-requests', label: 'Requests', icon: ClipboardList, testid: 'nav-leave-requests' },
      ],
    },
    {
      id: 'group-accounting', label: 'Accounting & Payroll', icon: Receipt, testid: 'nav-group-accounting',
      children: [
        { id: 'payroll', to: '/payroll', label: 'Payroll Runs', icon: ClipboardList, testid: 'nav-payroll' },
        { id: 'invoices', to: '/invoices', label: 'Invoices', icon: FileText, testid: 'nav-invoices', allowedRoles: ['company_admin', 'dispatcher'] },
        { id: 'corrections', to: '/payroll/corrections', label: 'Payroll Corrections', icon: MessageSquareWarning, testid: 'nav-corrections' },
        { id: 'escrow', to: '/escrow', label: 'Escrow Management', icon: PiggyBank, testid: 'nav-escrow' },
        { id: 'deferred', to: '/deferred-deductions', label: 'Deferred Deductions', icon: Layers, testid: 'nav-deferred' },
        { id: 'my-pay', to: '/my-pay', label: 'My Pay', icon: DollarSign, testid: 'nav-my-pay' },
        { id: 'my-advances', to: '/my-advances', label: 'My Advances', icon: DollarSign, testid: 'nav-my-advances' },
        { id: 'cost-settings', to: '/admin/cost-settings', label: 'Cost & Profitability', icon: TrendingUp, testid: 'nav-cost-settings' },
        { id: 'profit-dashboard', to: '/admin/profit-dashboard', label: 'Profit Dashboard', icon: TrendingUp, testid: 'nav-profit-dashboard' },
        { id: 'w9-forms', to: '/admin/w9', label: 'W-9 Forms', icon: FileText, testid: 'nav-w9-forms' },
        { id: 'form-1099', to: '/admin/form-1099', label: '1099-NEC Forms', icon: FileText, testid: 'nav-form-1099' },
        { id: 'ifta', to: '/ifta', label: 'IFTA Report', icon: FileText, testid: 'nav-ifta' },
        { id: 'billing', to: '/billing', label: 'Billing', icon: CreditCard, testid: 'nav-billing' },
      ],
    },
    {
      id: 'group-taxes', label: 'Taxes & Expenses', icon: Receipt, testid: 'nav-group-taxes',
      children: [
        { id: 'taxes-dashboard', to: '/taxes', label: 'Dashboard', icon: BarChart3, testid: 'nav-taxes-dashboard' },
        { id: 'taxes-expenses', to: '/taxes/expenses', label: 'Expenses', icon: Receipt, testid: 'nav-taxes-expenses' },
        { id: 'taxes-import', to: '/taxes/import', label: 'Import', icon: Upload, testid: 'nav-taxes-import' },
        { id: 'taxes-reimburse', to: '/taxes/reimburse', label: 'Reimbursements', icon: DollarSign, testid: 'nav-taxes-reimburse' },
        { id: 'taxes-reports', to: '/taxes/reports', label: 'Tax Preparer', icon: FileText, testid: 'nav-taxes-reports' },
        { id: 'taxes-assets', to: '/taxes/assets', label: 'Assets & Analysis', icon: Truck, testid: 'nav-taxes-assets' },
        { id: 'taxes-audit', to: '/taxes/audit', label: 'Audit Check', icon: CheckCircle, testid: 'nav-taxes-audit' },
        { id: 'taxes-settings', to: '/taxes/settings', label: 'Tax Settings', icon: Cog, testid: 'nav-taxes-settings' },
      ],
    },
    {
      id: 'group-comms', label: 'Communications', icon: MessageSquare, testid: 'nav-group-comms',
      children: [
        { id: 'messages', to: '/messages', label: 'Messages', icon: MessageSquare, testid: 'nav-messages' },
        { id: 'team-chat', to: '/team-chat', label: 'Team Chat', icon: MessagesSquare, testid: 'nav-team-chat' },
        { id: 'announcements', to: '/announcements', label: 'Announcements', icon: Megaphone, testid: 'nav-announcements' },
      ],
    },
    {
      id: 'group-insights', label: 'Analytics & Insights', icon: BarChart3, testid: 'nav-group-insights',
      children: [
        { id: 'analytics', to: '/analytics', label: 'Analytics', icon: BarChart3, testid: 'nav-analytics' },
        { id: 'telemetry', to: '/admin/telemetry', label: 'Usage Telemetry', icon: Activity, testid: 'nav-telemetry' },
      ],
    },
    {
      id: 'group-admin', label: 'Admin & System', icon: Cog, testid: 'nav-group-admin',
      children: [
        { id: 'settings', to: '/admin/settings', label: 'Settings', icon: SettingsIcon, testid: 'nav-admin-settings' },
        { id: 'branding', to: '/company/branding', label: 'Branding', icon: Palette, testid: 'nav-branding' },
        { id: 'feedback-inbox', to: '/feedback-inbox', label: 'Feedback Inbox', icon: MessageCircleQuestion, testid: 'nav-feedback-inbox' },
        { id: 'audit', to: '/audit-log', label: 'Audit Log', icon: ShieldCheck, testid: 'nav-audit' },
        { id: 'company-reset', to: '/company/reset', label: 'Reset My Company', icon: ShieldAlert, testid: 'nav-company-reset' },
      ],
    },
  ],
  dispatcher: [
    { id: 'dashboard', to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-dashboard' },
    {
      id: 'group-fleet-ops', label: 'Fleet Operations', icon: Truck, testid: 'nav-group-fleet-ops',
      children: [
        { id: 'loads', to: '/loads', label: 'Loads', icon: ClipboardList, testid: 'nav-loads' },
        { id: 'live-tracking', to: '/live-tracking', label: 'Live Tracking', icon: MapPin, testid: 'nav-live-tracking' },
        { id: 'tenders', to: '/tenders', label: 'Tender Inbox', icon: Inbox, testid: 'nav-tenders' },
        { id: 'drivers', to: '/drivers', label: 'Drivers', icon: Users, testid: 'nav-drivers' },
        { id: 'driver-requests', to: '/requests', label: 'Driver Requests', icon: Inbox, testid: 'nav-driver-requests' },
        { id: 'compliance', to: '/compliance', label: 'Compliance', icon: ShieldCheck, testid: 'nav-compliance' },
        { id: 'contracts', to: '/contracts', label: 'Contracts & Agreements', icon: FileSignature, testid: 'nav-contracts' },
        { id: 'brokers', to: '/brokers', label: 'Brokers', icon: Briefcase, testid: 'nav-brokers', permFeature: 'brokers' },
      ],
    },
    {
      id: 'group-assets', label: 'Asset Management', icon: Wrench, testid: 'nav-group-assets',
      children: [
        { id: 'assets', to: '/assets', label: 'Assets', icon: Wrench, testid: 'nav-assets' },
        { id: 'fleet-vehicles', to: '/fleet/vehicles', label: 'Fleet Vehicles (ELD)', icon: Truck, testid: 'nav-fleet-vehicles' },
        { id: 'truck_pnl', to: '/truck-pnl', label: 'Truck P&L', icon: TrendingUp, testid: 'nav-pnl', permFeature: 'truck_pnl' },
      ],
    },
    { id: 'my-pay', to: '/my-pay', label: 'My Pay', icon: DollarSign, testid: 'nav-my-pay' },
    { id: 'leave-requests', to: '/leave-requests', label: 'Leave Requests', icon: CalendarDays, testid: 'nav-leave-requests' },
    {
      id: 'group-comms', label: 'Communications', icon: MessageSquare, testid: 'nav-group-comms',
      children: [
        { id: 'messages', to: '/messages', label: 'Messages', icon: MessageSquare, testid: 'nav-messages' },
        { id: 'team-chat', to: '/team-chat', label: 'Team Chat', icon: MessagesSquare, testid: 'nav-team-chat' },
        { id: 'my-announcements', to: '/my-announcements', label: 'Announcements', icon: Megaphone, testid: 'nav-my-announcements' },
      ],
    },
    { id: 'analytics', to: '/analytics', label: 'Analytics', icon: BarChart3, testid: 'nav-analytics', permFeature: 'analytics' },
  ],
  driver: [
    { id: 'dashboard', to: '/dashboard', label: 'My Loads', icon: Truck, testid: 'nav-dashboard' },
    {
      id: 'group-me', label: 'My Workspace', icon: UserIcon, testid: 'nav-group-me',
      children: [
        { id: 'my-documents', to: '/my-documents', label: 'My Documents', icon: ShieldCheck, testid: 'nav-my-documents' },
        { id: 'my-agreements', to: '/contracts', label: 'My Agreements', icon: FileSignature, testid: 'nav-contracts' },
        { id: 'my-assets', to: '/my-assets', label: 'My Assets', icon: Wrench, testid: 'nav-my-assets' },
        { id: 'my-leave', to: '/my-leave', label: 'My Leave', icon: CalendarDays, testid: 'nav-my-leave' },
        { id: 'my-training', to: '/my-training', label: 'My Training', icon: GraduationCap, testid: 'nav-my-training' },
      ],
    },
    {
      id: 'group-accounting', label: 'My Pay', icon: Receipt, testid: 'nav-group-accounting',
      children: [
        { id: 'my-pay', to: '/my-pay', label: 'My Pay', icon: DollarSign, testid: 'nav-my-pay' },
        { id: 'my-advances', to: '/my-advances', label: 'My Advances', icon: DollarSign, testid: 'nav-my-advances' },
      ],
    },
    {
      id: 'group-comms', label: 'Communications', icon: MessageSquare, testid: 'nav-group-comms',
      children: [
        { id: 'messages', to: '/messages', label: 'Messages', icon: MessageSquare, testid: 'nav-messages' },
        { id: 'team-chat', to: '/team-chat', label: 'Team Chat', icon: MessagesSquare, testid: 'nav-team-chat' },
        { id: 'my-announcements', to: '/my-announcements', label: 'Announcements', icon: Megaphone, testid: 'nav-my-announcements' },
      ],
    },
    {
      id: 'group-insights', label: 'Insights', icon: BarChart3, testid: 'nav-group-insights',
      children: [
        { id: 'truck_pnl', to: '/truck-pnl', label: 'Truck P&L', icon: TrendingUp, testid: 'nav-pnl', permFeature: 'truck_pnl' },
        { id: 'brokers', to: '/brokers', label: 'Brokers', icon: Briefcase, testid: 'nav-brokers', permFeature: 'brokers' },
        { id: 'analytics', to: '/analytics', label: 'Analytics', icon: BarChart3, testid: 'nav-analytics', permFeature: 'analytics' },
      ],
    },
  ],
  accountant: [
    {
      id: 'group-taxes', label: 'Taxes & Expenses', icon: Receipt, testid: 'nav-group-taxes',
      children: [
        { id: 'taxes-dashboard', to: '/taxes', label: 'Dashboard', icon: BarChart3, testid: 'nav-taxes-dashboard' },
        { id: 'taxes-expenses', to: '/taxes/expenses', label: 'Expenses', icon: Receipt, testid: 'nav-taxes-expenses' },
        { id: 'taxes-import', to: '/taxes/import', label: 'Import', icon: Upload, testid: 'nav-taxes-import' },
        { id: 'taxes-reimburse', to: '/taxes/reimburse', label: 'Reimbursements', icon: DollarSign, testid: 'nav-taxes-reimburse' },
        { id: 'taxes-reports', to: '/taxes/reports', label: 'Tax Preparer', icon: FileText, testid: 'nav-taxes-reports' },
        { id: 'taxes-assets', to: '/taxes/assets', label: 'Assets & Analysis', icon: Truck, testid: 'nav-taxes-assets' },
        { id: 'taxes-audit', to: '/taxes/audit', label: 'Audit Check', icon: CheckCircle, testid: 'nav-taxes-audit' },
        { id: 'taxes-settings', to: '/taxes/settings', label: 'Tax Settings', icon: Cog, testid: 'nav-taxes-settings' },
      ],
    },
    { id: 'my-pay', to: '/my-pay', label: 'My Pay', icon: DollarSign, testid: 'nav-my-pay' },
  ],
};

const ROLE_LABEL = {
  super_admin: 'SUPER ADMIN',
  company_admin: 'COMPANY ADMIN',
  dispatcher: 'DISPATCHER',
  accountant: 'ACCOUNTANT / CPA',
  driver: 'DRIVER',
};

const avatarSrc = (user) => {
  if (!user?.avatar_url) return null;
  if (user.avatar_url.startsWith('http')) return user.avatar_url;
  return `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}`;
};

const Avatar = ({ user, size = 8, ringColor = 'ring-[#002FA7]/10' }) => {
  const src = avatarSrc(user);
  const demoRing = user?.is_demo ? 'ring-amber-400 ring-dashed' : ringColor;
  const cls = `w-${size} h-${size} rounded-full flex-shrink-0`;
  if (src) {
    return <img src={src} alt="" className={`${cls} object-cover ring-2 ${demoRing}`} data-testid={user?.is_demo ? 'demo-avatar' : undefined} />;
  }
  return (
    <div className={`${cls} bg-[#002FA7]/10 flex items-center justify-center text-[#002FA7] font-semibold uppercase ring-2 ${demoRing}`} style={{ fontFamily: 'Work Sans, sans-serif' }} data-testid={user?.is_demo ? 'demo-avatar' : undefined}>
      {(user?.name || user?.email || 'U').charAt(0)}
    </div>
  );
};

const NavLink = ({ item, currentPath, onItemClick, indented = false }) => {
  const Icon = item.icon;
  // Exact match always wins. Otherwise "startsWith" only when the segment after
  // matches a "/" boundary AND no sibling route is a closer prefix match.
  const exact = currentPath === item.to;
  const isPrefix = item.to !== '/dashboard'
    && currentPath.startsWith(item.to + '/');
  // For non-exact prefix matches, ensure a more-specific sibling isn't actually active.
  // Concretely: /payroll vs /payroll/corrections — only the more specific one should highlight.
  const moreSpecificPaths = ['/payroll/corrections']; // extend as needed
  const overshadowed = !exact && isPrefix && moreSpecificPaths.some(
    (p) => currentPath.startsWith(p) && p.length > (item.to || '').length
  );
  const active = exact || (isPrefix && !overshadowed);
  return (
    <Link
      to={item.to}
      onClick={onItemClick}
      data-testid={item.testid}
      className={`group flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all ${
        indented ? 'ml-4 pl-3 border-l border-slate-200' : ''
      } ${
        active
          ? 'bg-[#002FA7] text-white shadow-sm shadow-[#002FA7]/30'
          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
      }`}
      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
    >
      <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={2.25} />
      <span className="font-medium flex-1">{item.label}</span>
      {typeof item.badge === 'number' && item.badge > 0 && (
        <span
          data-testid={`${item.testid}-badge`}
          className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-bold rounded-full ${
            active
              ? 'bg-white text-[#002FA7]'
              : 'bg-red-500 text-white'
          }`}
          aria-label={`${item.badge} open`}
        >
          {item.badge > 99 ? '99+' : item.badge}
        </span>
      )}
      {active && <ChevronRight className="w-3.5 h-3.5" strokeWidth={2.5} />}
    </Link>
  );
};

const NavGroup = ({ item, currentPath, onItemClick }) => {
  const Icon = item.icon;
  const containsActive = (item.children || []).some(
    (c) => currentPath === c.to || (c.to && c.to !== '/dashboard' && currentPath.startsWith(c.to + '/'))
  );
  const [open, setOpen] = useState(containsActive);
  // Auto-open when the route changes to one of our children
  useEffect(() => {
    if (containsActive) setOpen(true);
  }, [containsActive]);
  // Sum child badges so a collapsed group still surfaces pending work.
  const aggregatedBadge = (item.children || []).reduce(
    (sum, c) => sum + (typeof c.badge === 'number' && c.badge > 0 ? c.badge : 0),
    0,
  );
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={item.testid}
        aria-expanded={open}
        className={`w-full group flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all ${
          containsActive && !open
            ? 'text-[#002FA7] bg-[#002FA7]/5'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
        }`}
        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
      >
        <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={2.25} />
        <span className="font-semibold flex-1 text-left">{item.label}</span>
        {!open && aggregatedBadge > 0 && (
          <span
            data-testid={`${item.testid}-badge`}
            className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-bold rounded-full bg-red-500 text-white"
            aria-label={`${aggregatedBadge} open`}
          >
            {aggregatedBadge > 99 ? '99+' : aggregatedBadge}
          </span>
        )}
        {open ? <ChevronDown className="w-3.5 h-3.5" strokeWidth={2.5} /> : <ChevronRight className="w-3.5 h-3.5" strokeWidth={2.5} />}
      </button>
      {open && (
        <div className="mt-1 space-y-1" data-testid={`${item.testid}-children`}>
          {(item.children || []).map((c) => (
            <NavLink key={c.id} item={c} currentPath={currentPath} onItemClick={onItemClick} indented />
          ))}
        </div>
      )}
    </div>
  );
};

const SideNav = ({ items, currentPath, onItemClick }) => (
  <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto" data-testid="side-nav">
    {items.map((item) => {
      if (item.children) {
        return <NavGroup key={item.id} item={item} currentPath={currentPath} onItemClick={onItemClick} />;
      }
      return <NavLink key={item.id} item={item} currentPath={currentPath} onItemClick={onItemClick} />;
    })}
  </nav>
);

const SidebarBrand = () => {
  const { branding } = useBranding();
  const hasCustomLogo = !!branding.logo_url;
  const hasCustomName = !!(branding.company_name && branding.company_name.trim());
  if (!hasCustomLogo && !hasCustomName) {
    // Default CivilX TMS — show the full wordmark; it already contains the brand name.
    return (
      <div className="px-5 py-5 border-b border-slate-200">
        <img
          src="/assets/civilx-tms-logo.png"
          alt="CivilX TMS"
          className="h-10 w-full max-w-[180px] object-contain object-left"
          data-testid="sidebar-company-logo"
        />
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 px-5 py-5 border-b border-slate-200">
      {hasCustomLogo && <CompanyLogo size={9} showName={false} testid="sidebar-company-logo" />}
      <div className="min-w-0">
        <h1 className="text-lg font-bold tracking-tight text-slate-900 leading-none truncate" style={{ fontFamily: 'Work Sans, sans-serif' }}>
          {branding.company_name || 'CivilX TMS'}
        </h1>
        <p className="text-[10px] text-slate-500 tracking-widest mt-1 truncate" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          {branding.dot_number ? `DOT ${branding.dot_number}` : 'FLEET MANAGEMENT'}
        </p>
      </div>
    </div>
  );
};

const SidebarUserCard = ({ user, onItemClick }) => (
  <Link
    to="/profile"
    onClick={onItemClick}
    data-testid="sidebar-profile-link"
    className="mx-3 mt-3 mb-1 flex items-center gap-3 px-3 py-3 rounded-md bg-gradient-to-br from-[#002FA7]/5 to-slate-50 hover:from-[#002FA7]/10 transition-all"
  >
    <Avatar user={user} size={11} />
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-slate-900 truncate" style={{ fontFamily: 'Work Sans, sans-serif' }}>{user.name || 'User'}</p>
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#002FA7] mt-0.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
        {ROLE_LABEL[user.role] || 'USER'}
      </p>
    </div>
    <UserIcon className="w-3.5 h-3.5 text-slate-400" />
  </Link>
);

const SidebarFooter = ({ onLogout }) => (
  <div className="border-t border-slate-200 px-3 py-3 space-y-1">
    <Link
      to="/settings"
      data-testid="nav-settings"
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all ${window.location.pathname === '/settings' ? 'bg-[#002FA7] text-white' : 'text-slate-600 hover:bg-slate-100'}`}
      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
    >
      <SettingsIcon className="w-4 h-4" strokeWidth={2.25} />
      <span className="font-medium">Settings</span>
    </Link>
    <button
      data-testid="logout-button"
      onClick={onLogout}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-slate-600 hover:bg-red-50 hover:text-red-700 transition-all"
      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
    >
      <LogOut className="w-4 h-4" strokeWidth={2.25} />
      <span className="font-medium">Logout</span>
    </button>
  </div>
);

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { subscription } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [perms, setPerms] = useState(null);
  const [openRequestsCount, setOpenRequestsCount] = useState(0);
  const [pendingAssetsCount, setPendingAssetsCount] = useState(0);
  const prevPermsRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    let mounted = true;
    const fetchPerms = async () => {
      try {
        const res = await axios.get(`${API}/company/settings`, { withCredentials: true });
        if (!mounted) return;
        const next = res.data.analytics_permissions;
        // Only toast on subsequent fetches where perms differ from what we last saw.
        // Skip toast for admins/super_admins because they're the ones changing settings.
        const isStaff = user.role !== 'company_admin' && user.role !== 'super_admin';
        if (prevPermsRef.current && isStaff) {
          try {
            const prev = JSON.stringify(prevPermsRef.current);
            const cur = JSON.stringify(next);
            if (prev !== cur) {
              toast.success('Settings updated — refreshing your view', {
                description: 'Your admin has changed analytics permissions for your role.',
                duration: 4500,
              });
            }
          } catch { /* ignore */ }
        }
        prevPermsRef.current = next;
        setPerms(next);
      } catch {
        // non-fatal — leave perms as-is
      }
    };
    fetchPerms();
    const onFocus = () => fetchPerms();
    window.addEventListener('focus', onFocus);
    return () => {
      mounted = false;
      window.removeEventListener('focus', onFocus);
    };
  }, [user?.id, user?.company_id, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll open Driver Requests count so dispatchers/admins get a live sidebar badge.
  // Kept lightweight: one small GET every 60s + on window focus + on route change.
  useEffect(() => {
    if (!user) return;
    const canSee = ['company_admin', 'dispatcher', 'super_admin'].includes(user.role);
    if (!canSee) return;
    let mounted = true;
    const fetchOpen = async () => {
      try {
        const res = await axios.get(`${API}/driver-requests`, { withCredentials: true });
        if (!mounted) return;
        const list = Array.isArray(res.data) ? res.data : [];
        const openCount = list.reduce(
          (n, r) => n + ((r?.status || 'open') === 'open' ? 1 : 0),
          0,
        );
        setOpenRequestsCount(openCount);
      } catch {
        // non-fatal — leave count as-is; sidebar just won't show a badge.
      }
    };
    fetchOpen();
    const interval = setInterval(fetchOpen, 60000);
    const onFocus = () => fetchOpen();
    window.addEventListener('focus', onFocus);
    return () => {
      mounted = false;
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [user?.id, user?.role, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll count of driver-submitted assets still needing review → sidebar badge.
  useEffect(() => {
    if (!user) return;
    if (!['company_admin', 'dispatcher', 'super_admin'].includes(user.role)) return;
    let mounted = true;
    const fetchPending = async () => {
      try {
        const res = await axios.get(`${API}/assets/pending-count`, { withCredentials: true });
        if (mounted) setPendingAssetsCount(res.data?.count || 0);
      } catch { /* non-fatal */ }
    };
    fetchPending();
    const interval = setInterval(fetchPending, 60000);
    const onFocus = () => fetchPending();
    window.addEventListener('focus', onFocus);
    return () => {
      mounted = false;
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [user?.id, user?.role, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (!user) return null;

  const rawItems = NAV_BY_ROLE[user.role] || [];
  const items = rawItems.filter((item) => {
    if (!item.permFeature) return true;
    // Only enforce on dispatcher/driver; admins always see everything
    if (user.role !== 'dispatcher' && user.role !== 'driver') return true;
    if (!perms) return false; // hide gated items until perms load
    return Boolean(perms[user.role]?.[item.permFeature]);
  });

  // Decorate the "Driver Requests" nav entry with a live open-count badge.
  // We walk one level deep since it currently only appears under grouped nav.
  const withBadge = (node) => {
    if (node.id === 'driver-requests') return { ...node, badge: openRequestsCount };
    if (node.id === 'assets') return { ...node, badge: pendingAssetsCount };
    return node;
  };
  // App Store 3.1.1: no billing / purchase entry points inside the native shell.
  const native = isNativeApp();
  const stripNative = (list) => (native ? list.filter((n) => n.id !== 'billing' && n.id !== 'subscriptions') : list);
  const decoratedItems = stripNative(items).map((item) =>
    item.children ? { ...item, children: stripNative(item.children).map(withBadge) } : withBadge(item),
  );

  return (
    <div className="min-h-screen bg-[#F2F4F7] flex">
      {/* Desktop sidebar */}
      <aside
        data-testid="sidebar"
        className="hidden lg:flex w-60 flex-col border-r border-slate-200 fixed inset-y-0 left-0 z-40"
        style={{ background: 'var(--brand-sidebar, #FFFFFF)' }}
      >
        <SidebarBrand />
        <SidebarUserCard user={user} />
        <SideNav items={decoratedItems} currentPath={location.pathname} />
        <SidebarFooter onLogout={handleLogout} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-slate-900/40 z-40"
            onClick={() => setMobileOpen(false)}
            data-testid="sidebar-backdrop"
          />
          <aside className="lg:hidden fixed inset-y-0 left-0 w-72 z-50 flex flex-col shadow-2xl" data-testid="sidebar-mobile" style={{ background: 'var(--brand-sidebar, #FFFFFF)', paddingTop: 'env(safe-area-inset-top)' }}>
            <SidebarBrand />
            <SidebarUserCard user={user} onItemClick={() => setMobileOpen(false)} />
            <SideNav items={decoratedItems} currentPath={location.pathname} onItemClick={() => setMobileOpen(false)} />
            <SidebarFooter onLogout={handleLogout} />
          </aside>
        </>
      )}

      {/* Right side / main */}
      <div className="flex-1 flex flex-col lg:ml-60 min-w-0">
        {/* Desktop top bar — just a slim notifications/profile strip */}
        <header className="hidden lg:flex sticky top-0 z-30 border-b border-slate-200 px-6 py-2.5 items-center justify-end gap-2" style={{ background: 'var(--brand-navbar, #FFFFFF)', paddingTop: 'calc(0.625rem + env(safe-area-inset-top))' }}>
          <ThemeToggle />
          <NotificationsBell />
        </header>
        {/* Mobile top bar */}
        <header className="lg:hidden border-b border-slate-200 sticky top-0 z-30" style={{ background: 'var(--brand-navbar, #FFFFFF)', paddingTop: 'env(safe-area-inset-top)' }}>
          <div className="px-4 py-3 flex items-center justify-between">
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen(true)}
              className="p-2 rounded-md hover:bg-slate-100 text-slate-700"
              aria-label="Open navigation"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="flex items-center gap-2 flex-1 min-w-0 px-2">
              <CompanyLogo size={8} testid="mobile-company-logo" className="min-w-0" />
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <ThemeToggle size="sm" />
              <NotificationsBell />
              <button onClick={() => navigate('/settings')} data-testid="mobile-profile-button" className="p-1">
                <Avatar user={user} size={9} />
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 pb-20 lg:pb-0">
          {!native && subscription?.status === 'trial' && subscription?.trial_ends_at && (() => {
            const ends = new Date(subscription.trial_ends_at);
            const now = new Date();
            const ms = ends.getTime() - now.getTime();
            const daysLeft = Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
            const urgent = daysLeft <= 3;
            return (
              <div
                data-testid="trial-banner"
                className={`px-4 py-2 text-center text-xs font-semibold border-b flex items-center justify-center gap-2 ${
                  urgent
                    ? 'bg-gradient-to-r from-red-50 via-red-100 to-red-50 text-red-900 border-red-200'
                    : 'bg-gradient-to-r from-blue-50 via-blue-100 to-blue-50 text-[#002FA7] border-blue-200'
                }`}
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${urgent ? 'bg-red-500' : 'bg-[#002FA7]'} animate-pulse`} />
                <span className="uppercase tracking-widest">Free Trial</span>
                <span data-testid="trial-banner-days" className="hidden sm:inline">
                  — {native
                    ? `${daysLeft} day${daysLeft === 1 ? '' : 's'} remaining · manage your plan at civilxtms.com`
                    : daysLeft === 0
                    ? 'Last day! Pick a plan now to keep your access.'
                    : daysLeft === 1
                      ? '1 day remaining — pick a plan to avoid lockout.'
                      : `${daysLeft} days remaining`}
                </span>
                <span className="sm:hidden">{daysLeft}d left</span>
                {!native && (user?.role === 'company_admin' || user?.role === 'super_admin') && (
                  <a
                    href="/billing"
                    data-testid="trial-banner-cta"
                    className="ml-2 underline hover:no-underline font-bold"
                  >
                    Pick a plan
                  </a>
                )}
              </div>
            );
          })()}
          <ReviewRoleSwitcher />
          <CompanyDeletionBanner />
          <TermsAcceptanceGate />
          {user?.is_demo && !user?.apple_review_persona && (
            <div
              data-testid="demo-mode-banner"
              className="bg-gradient-to-r from-amber-400 via-amber-300 to-amber-400 text-amber-950 px-4 py-2 text-center text-xs font-semibold border-b border-amber-500 flex items-center justify-center gap-2"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-700 animate-pulse" />
              <span className="uppercase tracking-widest">Demo Mode</span>
              <span className="hidden sm:inline">— You're in a sandboxed company. Data resets every 24h.</span>
              <button
                onClick={async () => {
                  try {
                    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' });
                  } catch { /* */ }
                  window.location.assign('/');
                }}
                className="ml-2 underline hover:no-underline font-bold"
                data-testid="demo-exit"
              >
                Exit demo
              </button>
            </div>
          )}
          {children}
        </main>
      </div>
      <BottomNav />
      <AnnouncementBroadcastCard />
    </div>
  );
};

export { Avatar, avatarSrc };
export default DashboardLayout;

// silence unused warning while keeping Camera available for sibling components
export const _CameraIcon = Camera;
